import { BoardState } from "../domain/types";
import { BoardAction, boardReducer } from "./boardReducer";

// Undo/redo wrapper around the board state.
export interface History {
    past: BoardState[];
    present: BoardState;
    future: BoardState[];
}

export type HistoryAction =
    // `reduce` optionally overrides the board reducer (e.g. an ownership-guarded
    // reducer on the patient board). Defaults to the plain boardReducer.
    | { type: "do"; action: BoardAction; reduce?: (s: BoardState, a: BoardAction) => BoardState }
    | { type: "undo" }
    | { type: "redo" }
    | { type: "reset"; state: BoardState }; // hydration — not undoable

const MAX_HISTORY = 100;

export function initHistory(present: BoardState): History {
    return { past: [], present, future: [] };
}

export function historyReducer(history: History, action: HistoryAction): History {
    switch (action.type) {
        case "reset":
            return { past: [], present: action.state, future: [] };
        case "do": {
            const reduce = action.reduce ?? boardReducer;
            const next = reduce(history.present, action.action);
            if (next === history.present) return history; // no-op
            const past = [...history.past, history.present].slice(-MAX_HISTORY);
            return { past, present: next, future: [] };
        }
        case "undo": {
            if (history.past.length === 0) return history;
            const previous = history.past[history.past.length - 1];
            return {
                past: history.past.slice(0, -1),
                present: previous,
                future: [history.present, ...history.future],
            };
        }
        case "redo": {
            if (history.future.length === 0) return history;
            const [next, ...rest] = history.future;
            return {
                past: [...history.past, history.present],
                present: next,
                future: rest,
            };
        }
        default:
            return history;
    }
}

export const canUndo = (h: History): boolean => h.past.length > 0;
export const canRedo = (h: History): boolean => h.future.length > 0;
